import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { receptionsAPI, debugConnection } from '../services/api';
import ReceptionModal from './ReceptionModal';
import { formatDate } from '../utils/dateUtils';
import './ReceptionsList.css';

const EMPTY_FILTERS = { supplier: '', ir: '', oc: '', search: '', dateFrom: '', dateTo: '' };

function ReceptionsList() {
  const [receptions, setReceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  /* eslint-disable no-unused-vars */
  const [error, setError] = useState(null);
  /* eslint-enable no-unused-vars */
  const [showModal, setShowModal] = useState(false);
  const [alert, setAlert] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searching, setSearching] = useState(false);
  const [articleSummary, setArticleSummary] = useState([]);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'code', direction: 'asc' });
  const debounceRef = useRef(null);

  // Convertir YYYY-MM-DD a YYYYMMDD
  const toYMD = (d) => d ? d.replace(/-/g, '') : '';

  useEffect(() => {
    debugConnection();
    loadReceptions({}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReceptions = useCallback(async (f = {}, isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      setSearching(true);
      const response = await receptionsAPI.getAll({
        search: f.search || undefined,
        supplier: f.supplier || undefined,
        ir: f.ir || undefined,
        oc: f.oc || undefined,
        dateFrom: f.dateFrom || undefined,
        dateTo: f.dateTo || undefined,
      });
      setReceptions(response.data);
      setArticleSummary(response.article_summary || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, []);

  const triggerSearch = useCallback((f) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadReceptions({
        search: f.search.trim(),
        supplier: f.supplier.trim(),
        ir: f.ir.trim(),
        oc: f.oc.trim(),
        dateFrom: toYMD(f.dateFrom),
        dateTo: toYMD(f.dateTo),
      });
    }, 500);
  }, [loadReceptions]);

  const handleFilterChange = (field) => (e) => {
    const next = { ...filters, [field]: e.target.value };
    setFilters(next);
    triggerSearch(next);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setArticleSummary([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    loadReceptions({});
  };

  const handleCreate = async (data) => {
    try {
      await receptionsAPI.create(data);
      setShowModal(false);
      setAlert({ type: 'success', message: 'Ingreso creado correctamente' });
      loadReceptions();
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      setAlert({ type: 'error', message: err.message });
      setTimeout(() => setAlert(null), 5000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este ingreso?')) {
      return;
    }

    try {
      await receptionsAPI.delete(id);
      setAlert({ type: 'success', message: 'Ingreso eliminado correctamente' });
      loadReceptions();
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      setAlert({ type: 'error', message: err.message });
      setTimeout(() => setAlert(null), 5000);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedArticleSummary = useMemo(() => {
    if (articleSummary.length === 0) return [];

    const sorted = [...articleSummary].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [articleSummary, sortConfig]);

  const isSearchActive = searching && !loading;

  const renderReceptionsTable = () => (
    <div className="card table-card">
      <table className="table receptions-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Fecha</th>
            <th>Proveedor</th>
            <th>CUIT</th>
            <th>Detalle (IRs / Facturas / OCs)</th>
            <th>Total Ingresos (IR)</th>
            <th>Total Facturas</th>
            <th>Diferencia</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {isSearchActive ? (
            <tr>
              <td colSpan="9" style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
                ⏳ Buscando...
              </td>
            </tr>
          ) : receptions.length === 0 ? (
            <tr>
              <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>
                {hasActiveFilters ? 'No se encontraron ingresos para la búsqueda.' : 'No hay ingresos. Crea uno nuevo para comenzar.'}
              </td>
            </tr>
          ) : (
            receptions.map((reception) => (
              <tr key={reception.id}>
                <td data-label="ID">{reception.id}</td>
                <td data-label="Fecha">{formatDate(reception.reception_date)}</td>
                <td data-label="Proveedor">
                  <div>
                    <strong>{reception.supplier_name || '-'}</strong>
                    {reception.supplier_id && (
                      <small style={{ display: 'block', color: '#666', fontSize: '11px' }}>
                        ID: {reception.supplier_id}
                      </small>
                    )}
                  </div>
                </td>
                <td data-label="CUIT">{reception.supplier_legal_number || '-'}</td>
                <td data-label="Detalle" className="detail-cell">
                  {reception.irs && reception.irs.length > 0 ? (
                    <div className="detail-section">
                      <span className="detail-label">IRs:</span>
                      <div className="detail-chips">
                        {reception.irs.map((ir) => (
                          <span key={ir.ir_id} className="chip chip-ir" title={`IR ${ir.ir_id} — OCs: ${ir.ocs.length > 0 ? ir.ocs.join(', ') : 'ninguna'}`}>
                            IR {ir.ir_id}
                            {ir.ocs.length > 0 && (
                              <span className="chip-sub"> → OC {ir.ocs.join(', ')}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="detail-section">
                      <span className="detail-label" style={{ color: '#999' }}>Sin IRs</span>
                    </div>
                  )}
                  {reception.invoices && reception.invoices.length > 0 ? (
                    <div className="detail-section" style={{ marginTop: '6px' }}>
                      <span className="detail-label">Fact:</span>
                      <div className="detail-chips">
                        {reception.invoices.map((inv, idx) => (
                          <span key={idx} className="chip chip-invoice">
                            {inv.invoice_type ? `(${inv.invoice_type}) ` : ''}{inv.invoice_number}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="detail-section" style={{ marginTop: '6px' }}>
                      <span className="detail-label" style={{ color: '#999' }}>Sin Facturas</span>
                    </div>
                  )}
                  {reception.destinations && reception.destinations.length > 0 && (
                    <div className="detail-section" style={{ marginTop: '6px' }}>
                      <span className="detail-label">Destino:</span>
                      <div className="detail-chips">
                        {reception.destinations.map((dest, idx) => (
                          <span key={idx} className="chip chip-destination" title={`ID: ${dest.office_id}`}>
                            📍 {dest.office_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {reception.matched_articles && reception.matched_articles.length > 0 && (
                    <div className="detail-section" style={{ marginTop: '6px' }}>
                      <span className="detail-label">Artículos:</span>
                      <div className="detail-chips">
                        {reception.matched_articles.map((art, idx) => (
                          <span key={idx} className="chip chip-article" title={art.description}>
                            <strong>{art.code}</strong>
                            <span className="chip-sub"> {art.description.length > 40 ? art.description.substring(0, 40) + '…' : art.description}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </td>
                <td data-label="Total Ingresos (IR)">${(reception.ir_total_value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td data-label="Total Facturas">${parseFloat(reception.total_invoices || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                <td data-label="Diferencia" style={{
                  color: Math.abs(reception.financial_difference) < 0.01 ? 'green' : 'red',
                  fontWeight: 'bold'
                }}>
                  ${(reception.financial_difference || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td data-label="Acciones" className="actions-cell">
                  <div className="action-buttons">
                    <Link to={`/reception/${reception.id}`} className="btn btn-primary btn-sm">
                      Ver
                    </Link>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(reception.id)}
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return <div className="loading">Cargando ingresos...</div>;
  }

  return (
    <div className={`receptions-list ${articleSummary.length > 0 ? 'split-active' : ''}`}>
      <div className="header">
        <h1>Gestión de Ingresos</h1>
        <p>Sistema de control de ingresos y facturas</p>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.message}
        </div>
      )}

      <div className="filters-bar">
        <div className="filters-row filters-search">
          <button className="btn btn-primary btn-new" onClick={() => setShowModal(true)}>
            + Nuevo Ingreso
          </button>
          <div className="search-field">
            <label htmlFor="filter-supplier">Proveedor</label>
            <input
              id="filter-supplier"
              type="text"
              className="search-input"
              placeholder="Nombre, CUIT o ID..."
              value={filters.supplier}
              onChange={handleFilterChange('supplier')}
            />
          </div>
          <div className="search-field search-field-sm">
            <label htmlFor="filter-ir">Nro IR</label>
            <input
              id="filter-ir"
              type="text"
              className="search-input"
              placeholder="Ej: 12345"
              value={filters.ir}
              onChange={handleFilterChange('ir')}
            />
          </div>
          <div className="search-field search-field-sm">
            <label htmlFor="filter-oc">Nro OC</label>
            <input
              id="filter-oc"
              type="text"
              className="search-input"
              placeholder="Ej: 6789"
              value={filters.oc}
              onChange={handleFilterChange('oc')}
            />
          </div>
          <div className="search-field search-field-wide">
            <label htmlFor="filter-search">Factura / Artículo</label>
            <input
              id="filter-search"
              type="text"
              className="search-input"
              placeholder="Nro factura, código o descripción de artículo..."
              value={filters.search}
              onChange={handleFilterChange('search')}
            />
          </div>
        </div>
        <div className="filters-row filters-dates">
          <label className="date-label">
            Desde:
            <input type="date" className="date-input" value={filters.dateFrom} onChange={handleFilterChange('dateFrom')} />
          </label>
          <label className="date-label">
            Hasta:
            <input type="date" className="date-input" value={filters.dateTo} onChange={handleFilterChange('dateTo')} />
          </label>
          {hasActiveFilters && (
            <button className="btn btn-secondary btn-sm btn-clear-filters" onClick={clearFilters}>
              ✕ Limpiar filtros
            </button>
          )}
          {searching && <span className="search-spinner">⏳</span>}
        </div>
      </div>

      {articleSummary.length > 0 ? (
        <div className="split-container">
          <div className="split-top">
            {renderReceptionsTable()}
          </div>
          <div className={`split-bottom ${summaryOpen ? '' : 'collapsed'}`}>
            <div
              className="summary-header"
              onClick={() => setSummaryOpen(o => !o)}
              title={summaryOpen ? 'Ocultar resumen' : 'Mostrar resumen'}
            >
              <span className="summary-toggle">{summaryOpen ? '▾' : '▸'}</span>
              <span className="summary-icon">📦</span>
              <h3>Resumen de Artículos Matcheados</h3>
              <span className="summary-count">{articleSummary.length} artículo{articleSummary.length !== 1 ? 's' : ''}</span>
            </div>
            {summaryOpen && (
            <div className="summary-table-wrap">
              <table className="summary-table">
                <thead>
                  <tr>
                    <th className="sortable-header" onClick={() => handleSort('code')}>
                      Código {sortConfig.key === 'code' && <span className="sort-icon">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('description')}>
                      Descripción {sortConfig.key === 'description' && <span className="sort-icon">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('total_qty')}>
                      Cantidad Total Recibida {sortConfig.key === 'total_qty' && <span className="sort-icon">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedArticleSummary.map((art, idx) => (
                    <tr key={idx}>
                      <td className="summary-code">{art.code}</td>
                      <td>{art.description}</td>
                      <td className="summary-qty">{art.total_qty.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>
      ) : (
        renderReceptionsTable()
      )}

      {showModal && (
        <ReceptionModal
          onClose={() => setShowModal(false)}
          onSave={handleCreate}
        />
      )}
    </div>
  );
}

export default ReceptionsList;
